// useTool: fetch a single helper-server tool envelope from the canvas
// backend's /tool/{name} proxy. Re-runs when args change.
//
// Usage:
//   const { env, error, loading, refetch } = useTool("project_dashboard", {});
//   if (loading) return <Skeleton />;
//   if (error) return <ErrorBox />;
//   return <ArtifactView envelope={env!} />;
//
// Bypasses the LLM entirely — no Anthropic tokens spent — so this is the
// right path for view-mounted data fetches (every view in the new shell).

import { useCallback, useEffect, useState } from "react";
import type { Envelope } from "./envelope.js";

interface State {
  env: Envelope | null;
  error: string | null;
  loading: boolean;
}

/**
 * useTool — fetch a helper-server envelope via the canvas /tool proxy.
 *
 * Pass `null` for `args` to skip the fetch (e.g. while no row is selected,
 * to avoid a noisy "not found" envelope on initial render). When args
 * transitions from null → object the fetch fires; back to null clears state.
 */
export function useTool(
  toolName: string,
  args: Record<string, unknown> | null = {},
): State & { refetch: () => Promise<void> } {
  const skip = args === null;
  const argsKey = skip ? "__skip__" : JSON.stringify(args);
  const [state, setState] = useState<State>({ env: null, error: null, loading: !skip });

  const refetch = useCallback(async () => {
    if (skip) {
      setState({ env: null, error: null, loading: false });
      return;
    }
    setState((s) => ({ ...s, loading: true, error: null }));
    let resp: Response;
    try {
      resp = await fetch(`/tool/${encodeURIComponent(toolName)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(args),
      });
    } catch (e) {
      setState({ env: null, error: String(e), loading: false });
      return;
    }
    let body: unknown;
    try {
      body = await resp.json();
    } catch {
      setState({ env: null, error: `non-JSON response (status ${resp.status})`, loading: false });
      return;
    }
    if (!resp.ok) {
      const errMsg = (body as { error?: string })?.error ?? `status ${resp.status}`;
      setState({ env: null, error: errMsg, loading: false });
      return;
    }
    const env = body as Envelope;
    if (!env.artifact_type) {
      setState({ env: null, error: "response has no artifact_type", loading: false });
      return;
    }
    setState({ env, error: null, loading: false });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [toolName, argsKey, skip]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { ...state, refetch };
}
